import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { MapPin, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { ListEmptyState } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { focusCompetitorSearch } from '@/core/components/reputation/competitors/competitor-tracked-card';
import type { CompetitorTheaterLabels } from '@/core/components/reputation/competitor-theater';
import { CompetitorTheater } from '@/core/components/reputation/competitor-theater';
import {
  ReputationLayout,
  ReputationPageBanners,
} from '@/core/components/reputation/reputation-layout';
import { ReputationPageBreadcrumb } from '@/core/components/reputation/reputation-page-breadcrumb';
import { useListCompetitors } from '@/core/hooks/use-competitor-benchmark';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useReputationOverview } from '@/core/hooks/use-reputation';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

const LOCATIONS_LIMIT = 200;

export function ReputationCompetitorsPage() {
  return (
    <AppIonPage>
      <AppPageContent>
        <ReputationLayout>
          <ReputationCompetitorsPageContent />
        </ReputationLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function ReputationCompetitorsPageContent() {
  const dict = useDictionary();
  const d = dict.reputation?.competitorsPage;
  const setupLabels = dict.reputation?.setup?.competitors;
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations(workspaceId, { limit: LOCATIONS_LIMIT });
  const routerLocation = useLocation();
  const { navigateShell } = useNavigateShell();

  const locationIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(routerLocation.search);
    return params.get('locationId') ?? '';
  }, [routerLocation.search]);

  // Optimistic selection while shell navigation updates the query string.
  const [pendingLocationId, setPendingLocationId] = useState<string | null>(
    null,
  );
  if (pendingLocationId !== null && pendingLocationId === locationIdFromQuery) {
    setPendingLocationId(null);
  }
  const selectedLocationId = pendingLocationId ?? locationIdFromQuery;

  const effectiveLocationId = useMemo(() => {
    if (
      selectedLocationId &&
      locations.some((loc) => loc.id === selectedLocationId)
    ) {
      return selectedLocationId;
    }
    return locations[0]?.id ?? '';
  }, [locations, selectedLocationId]);

  const onLocationChange = (id: string) => {
    setPendingLocationId(id);
    const params = new URLSearchParams();
    if (id) params.set('locationId', id);
    const qs = params.toString();
    navigateShell(
      qs
        ? `${shellPaths.reputationCompetitors}?${qs}`
        : shellPaths.reputationCompetitors,
      { intent: 'replaceFlat' },
    );
  };

  const overview = useReputationOverview(workspaceId, {
    locationId: effectiveLocationId || undefined,
  });
  // A location without reviews has no rating (not 0.0).
  const hasOwnReviews = (overview.data?.totalReviews ?? 0) > 0;
  const yourAverageRating = hasOwnReviews
    ? overview.data?.averageRating
    : undefined;

  const labels: CompetitorTheaterLabels = {
    limitation: setupLabels?.limitation ?? '',
    limitationLabel: setupLabels?.limitationLabel ?? '',
    remove: setupLabels?.remove ?? '',
    limitReached: setupLabels?.limitReached ?? '',
    addError: setupLabels?.addError ?? '',
    rating: setupLabels?.rating ?? '',
    reviews: setupLabels?.reviews ?? '',
    mapUnavailable: setupLabels?.mapUnavailable ?? '',
    yourLocation: setupLabels?.yourLocation ?? '',
    track: setupLabels?.track ?? '',
    tracked: setupLabels?.tracked ?? '',
    noCoordinates: setupLabels?.noCoordinates ?? '',
    unsupportedPreset: setupLabels?.unsupportedPreset ?? '',
    nearbyEmpty: setupLabels?.nearbyEmpty ?? '',
    nearbyError: setupLabels?.nearbyError ?? '',
    radiusHint: setupLabels?.radiusHint ?? '',
    radiusHintTracked: setupLabels?.radiusHintTracked ?? '',
    searchHeading: setupLabels?.searchHeading ?? '',
    close: setupLabels?.close ?? '',
    vsYou: d?.vsYou ?? '{delta} vs you',
    vsYouEven: d?.vsYouEven ?? 'same as you',
    legendTracked: setupLabels?.legendTracked ?? '',
    legendSuggested: setupLabels?.legendSuggested ?? '',
    youShort: setupLabels?.youShort ?? '',
    legendThreat: setupLabels?.legendThreat ?? '',
    legendAhead: setupLabels?.legendAhead ?? '',
    legendEven: setupLabels?.legendEven ?? '',
    clusterCount: setupLabels?.clusterCount ?? '{count}',
    recenterMap: setupLabels?.recenterMap ?? 'Show all pins',
    sampleReviews: d?.sampleReviews ?? '',
    removed: d?.removed ?? '',
    undo: d?.undo ?? '',
  };

  const competitorsList = useListCompetitors(
    workspaceId,
    effectiveLocationId || undefined,
  );
  const trackedCount = competitorsList.data?.competitors.length ?? 0;
  const locationName =
    locations.find((loc) => loc.id === effectiveLocationId)?.name ??
    effectiveLocationId;

  const locationSelect = (
    <div className="space-y-1.5">
      <Label htmlFor="competitors-location" className="sr-only">
        {d?.locationLabel ?? 'Location'}
      </Label>
      <Select value={effectiveLocationId} onValueChange={onLocationChange}>
        <SelectTrigger id="competitors-location" className="w-full">
          <SelectValue placeholder={d?.locationLabel ?? 'Location'} />
        </SelectTrigger>
        <SelectContent>
          {locations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id}>
              {loc.name ?? loc.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <AppPageLayout className="min-w-0 max-w-full">
      <PageHeader
        title={d?.title ?? 'Competitors'}
        breadcrumb={
          <ReputationPageBreadcrumb
            pageLabel={dict.nav.reputationCompetitors}
          />
        }
        eyebrow={
          effectiveLocationId
            ? `${locationName} · ${d.trackedCount.replace('{count}', String(trackedCount))}`
            : undefined
        }
        actions={
          locations.length > 0 ? (
            <>
              <div className="hidden w-64 md:block">{locationSelect}</div>
              <Button onClick={focusCompetitorSearch}>
                <Plus className="mr-2 size-4" aria-hidden />
                <span className="md:hidden">{d.trackShort}</span>
                <span className="max-md:hidden">{d.track}</span>
              </Button>
            </>
          ) : undefined
        }
      />
      <ReputationPageBanners />
      {locations.length > 0 ? (
        <div className="md:hidden">{locationSelect}</div>
      ) : null}
      {!workspaceId ? null : locations.length === 0 ? (
        <ListEmptyState
          icon={<MapPin className="h-5 w-5" />}
          title={d?.noLocations ?? setupLabels?.noLocations ?? ''}
        />
      ) : effectiveLocationId ? (
        <CompetitorTheater
          workspaceId={workspaceId}
          locationId={effectiveLocationId}
          locationName={
            locations.find((loc) => loc.id === effectiveLocationId)?.name ??
            effectiveLocationId
          }
          yourAverageRating={yourAverageRating}
          yourTotalReviews={overview.data?.totalReviews}
          labels={labels}
        />
      ) : null}
    </AppPageLayout>
  );
}
